/**
 * Booking cut-off helpers — Feb 2026 spec.
 *
 * Rules:
 *   • Class booking stays OPEN until 15 minutes before start.
 *   • From (start - 15 min) up to `start`, booking is CLOSED (grey button,
 *     WhatsApp Jason for late registration).
 *   • Once `now >= start`, the class is considered STARTED and should be
 *     filtered out of Upcoming Classes entirely.
 *
 * Times are compared in Malaysia (Asia/Kuala_Lumpur, UTC+8). Since Malaysia
 * has no DST, we can rely on a fixed +8 offset when the API returns a
 * naive `date` (YYYY-MM-DD) + `time` (HH:MM). Server-side authoritative
 * enforcement lives in `_class_booking_status` in backend/server.py.
 */

// Malaysia = UTC+8, no DST. `date` + `time` from the API are Malaysia local.
const MALAYSIA_UTC_OFFSET_MIN = 8 * 60;

/** Return a UTC-anchored Date for a class's start moment.
 *  `date` and `time` are Malaysia local wall-clock strings.
 *  Returns `null` if inputs are missing/invalid. */
export const classStartUtc = (cls) => {
  if (!cls || !cls.date || !cls.time) return null;
  const m = /^(\d{2}):(\d{2})/.exec(cls.time);
  if (!m) return null;
  const [y, mo, d] = cls.date.split('-').map(Number);
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  // Interpret (y, mo, d, hh, mm) as Malaysia local -> convert to UTC by
  // subtracting +08:00.
  const utcMs = Date.UTC(y, (mo || 1) - 1, d || 1, hh, mm) - MALAYSIA_UTC_OFFSET_MIN * 60_000;
  return new Date(utcMs);
};

/** Booking status for a class.
 *  Returns one of:
 *    { key: 'open',    ... }  — normal booking flow
 *    { key: 'cutoff',  ... }  — grey "Booking Closed" + WhatsApp Jason
 *    { key: 'started', ... }  — class has started but hasn't finished yet
 *                               (still hide from Upcoming Classes lists;
 *                                on the detail page render "Booking Closed"
 *                                since late-add via WhatsApp is still possible)
 *    { key: 'ended',   ... }  — class has finished — show "Class Ended", hide WhatsApp
 *  Falls back to `open` if the class lacks date/time. */
export const bookingCutoffStatus = (cls, now = new Date()) => {
  const startUtc = classStartUtc(cls);
  if (!startUtc) return { key: 'open', minsToStart: null, startUtc: null, endUtc: null };
  const endUtc = classEndUtc(cls);
  const minsToStart = Math.floor((startUtc.getTime() - now.getTime()) / 60_000);
  // Ended when the class's end time has passed. If the class doesn't
  // publish an end_time we assume a 60-minute default duration.
  if (endUtc && now.getTime() >= endUtc.getTime()) {
    return { key: 'ended', minsToStart, startUtc, endUtc };
  }
  if (minsToStart <= 0) return { key: 'started', minsToStart, startUtc, endUtc };
  if (minsToStart <= 15) return { key: 'cutoff', minsToStart, startUtc, endUtc };
  return { key: 'open', minsToStart, startUtc, endUtc };
};

/** Return a UTC-anchored Date for a class's end moment.
 *  Uses `end_time` (HH:MM Malaysia local) when the API publishes it, else
 *  falls back to start + 60 minutes so old classes still transition to
 *  "Class Ended" reliably. */
export const classEndUtc = (cls) => {
  const startUtc = classStartUtc(cls);
  if (!startUtc) return null;
  const endStr = cls?.end_time;
  const m = /^(\d{2}):(\d{2})/.exec(endStr || '');
  if (m) {
    const [y, mo, d] = cls.date.split('-').map(Number);
    const utcMs = Date.UTC(y, (mo || 1) - 1, d || 1, Number(m[1]), Number(m[2])) - MALAYSIA_UTC_OFFSET_MIN * 60_000;
    // Guard against end < start (e.g. bad data). Fall back to +60 min.
    if (utcMs > startUtc.getTime()) return new Date(utcMs);
  }
  return new Date(startUtc.getTime() + 60 * 60_000);
};

/** Return true if the class is bookable RIGHT NOW (i.e. not started, not in
 *  the 15-min cut-off window). Server-side is authoritative — this is a UI
 *  hint only. */
export const isBookingOpen = (cls, now = new Date()) =>
  bookingCutoffStatus(cls, now).key === 'open';

/** Return true if the class start is in the past (or within 0 minutes). Used
 *  to filter Upcoming Classes on every page that lists them. */
export const isClassStarted = (cls, now = new Date()) => {
  const s = bookingCutoffStatus(cls, now).key;
  return s === 'started' || s === 'ended';
};

/** Return true if the class has completely finished — used by the class
 *  detail page to swap the WhatsApp button out for a passive "Class Ended"
 *  chip (there's no point WhatsApping about a class that's already over). */
export const isClassEnded = (cls, now = new Date()) =>
  bookingCutoffStatus(cls, now).key === 'ended';

// Jason's WhatsApp deep link. Never change without confirming with the admin.
export const JASON_WHATSAPP_BASE = 'https://wa.link/41dea7';
// Fallback that lets us attach a pre-filled text via `?text=…` when the
// wa.link short URL doesn't support text params on the destination device.
// wa.me is the universal WhatsApp deep-link.
const JASON_PHONE_INTL = '60162554068'; // Admin Jason Fong · +60 16-255 4068

/** Build a WhatsApp URL that pre-fills a message asking Jason to allow late
 *  registration for a specific class.
 *  If the wa.link short URL is passed (default), we return it AS-IS because
 *  wa.link redirects can't reliably carry `?text=`. When you need the text
 *  pre-filled reliably, switch to the `wa.me` URL by passing `{via: 'wa.me'}`.
 *  We default to `wa.me` here so the pre-fill always works. */
export const buildJasonWhatsappUrl = (className, dateLabel, timeLabel, { via = 'wa.me' } = {}) => {
  const text = `Hi Jason, I would like to join ${className} on ${dateLabel} at ${timeLabel}. Is late registration still available?`;
  if (via === 'wa.link') return JASON_WHATSAPP_BASE;
  return `https://wa.me/${JASON_PHONE_INTL}?text=${encodeURIComponent(text)}`;
};
